import { type FunctionComponent, useMemo } from 'react';
import classNames from 'classnames';
import { Box, Card, Skeleton, Stack, Typography } from '@mui/material';

import StarCircle from 'components/Icons/StarCircle';
import ForecasterProfile from 'components/ForecasterProfile';
import SubregionForecastLink from 'components/SubregionForecastLink';
import type { ForecastContent } from 'types/subregion';

import styles from './SubregionForecastContentNow.module.scss';

interface Props {
  className?: string;
  elevation?: number;
  forecastContent?: ForecastContent;
  forecastNote?: string | null;
  isLoading?: boolean;
  isTitleOnly?: boolean;
  pageName?: string;
}

const SubregionForecastContentNow: FunctionComponent<Props> = ({
  className,
  elevation,
  forecastContent,
  forecastNote,
  isLoading,
  isTitleOnly = false,
  pageName,
}) => {
  const nowTitle = useMemo(() => {
    if (!forecastContent) return null;
    const forecast = forecastContent.tomorrowIsLive
      ? forecastContent.tomorrow.forecast
      : forecastContent.today.forecast;
    return (
      <>
        {forecast.dayToWatch && <StarCircle className={styles.star} />}{' '}
        {forecastContent.tomorrowIsLive ? 'Tomorrow' : 'Today'}
      </>
    );
  }, [forecastContent]);

  if (isLoading)
    return (
      <Skeleton
        animation="wave"
        className={styles.loadingNow}
        data-testid="subregion-forecast-content-now-loading"
        variant="rectangular"
      />
    );

  if (!forecastContent) return null;

  return (
    <Card
      className={classNames(styles.forecasterContentNow, className)}
      component="section"
      elevation={elevation}
      data-testid="subregion-forecast-content-now"
    >
      <Box padding={1}>
        <Stack component="header" direction="row" mb={2}>
          <Box sx={{ width: '100%' }}>
            <ForecasterProfile
              conditions=""
              iconUrl={forecastContent.forecaster.avatarUrl}
              isShowConditionsBorder={false}
              isShowName
              lastUpdate={{ timestamp: forecastContent.lastPublished }}
              name={forecastContent.forecaster.name}
              title={nowTitle}
            />
          </Box>
          <Box>
            <SubregionForecastLink
              contentBlock={forecastContent.tomorrowIsLive ? 'tomorrow' : 'today'}
              forecasterName={forecastContent.forecaster.name}
              pageName={pageName}
              start={forecastContent.tomorrowIsLive ? 'forecast-tomorrow' : 'forecast-today'}
              subregionId={forecastContent.subregion.id}
              subregionName={forecastContent.subregion.name}
            />
          </Box>
        </Stack>
        <Typography component="h5" variant="body" mb={2}>
          {forecastContent.tomorrowIsLive
            ? forecastContent.tomorrow.forecast.headline
            : forecastContent.today.forecast.headline}
        </Typography>
        {!isTitleOnly && (
          <Typography component="p" variant="body1">
            <span
              dangerouslySetInnerHTML={{
                __html: forecastContent.tomorrowIsLive
                  ? forecastContent.tomorrow.forecast.observation
                  : forecastContent.today.forecast.observation,
              }}
            />
          </Typography>
        )}
        {!isTitleOnly && forecastNote && (
          <Typography className={styles.forecastNotes} component="div" variant="body1">
            {}
            <div dangerouslySetInnerHTML={{ __html: forecastNote }} />
          </Typography>
        )}
      </Box>
    </Card>
  );
};

export default SubregionForecastContentNow;
